import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  return "$" + Math.round(n).toLocaleString("en-US");
}

export default function CoverageCalculator() {
  const [income, setIncome] = useState(85000);
  const [dependents, setDependents] = useState(2);
  const [debts, setDebts] = useState(250000);
  const [existing, setExisting] = useState(0);
  const [years, setYears] = useState(10);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/calculator`, {
        annual_income: income,
        dependents,
        debts,
        existing_coverage: existing,
        years_to_replace: years,
      });
      setResult(data);
    } catch (e) {
      toast.error("Could not calculate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const rows = [
    { label: "Annual income", value: income, min: 20000, max: 500000, step: 5000, set: setIncome, fmt },
    { label: "Dependents", value: dependents, min: 0, max: 8, step: 1, set: setDependents, fmt: (v) => v },
    { label: "Debts & mortgage", value: debts, min: 0, max: 2000000, step: 10000, set: setDebts, fmt },
    { label: "Existing coverage", value: existing, min: 0, max: 2000000, step: 10000, set: setExisting, fmt },
    { label: "Years to replace income", value: years, min: 5, max: 30, step: 1, set: setYears, fmt: (v) => `${v} yrs` },
  ];

  return (
    <section id="calculator" className="bg-cream border-t border-ink/10" data-testid="calculator-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="text-ink/40 text-[11px] tracking-[0.3em] mb-8 flex items-center gap-4">
            <span className="w-8 h-[1px] bg-ink/30" />
            CHAPTER 05 · THE MATH
          </div>
          <h2 className="font-serif text-ink text-5xl md:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tight mb-8">
            How much is <span className="italic text-terracotta">enough?</span>
          </h2>
          <p className="text-ink/70 text-base md:text-lg leading-relaxed max-w-md font-light">
            Answer five honest questions. We'll model an unbiased coverage
            estimate using the DIME method — Debt, Income, Mortgage, Education.
          </p>
          <div className="mt-12 hidden lg:block">
            <div className="text-[10px] tracking-[0.3em] text-ink/40 mb-3">SUGGESTED COVERAGE</div>
            <motion.div
              key={result?.suggested_coverage ?? "empty"}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="font-serif text-ink text-7xl xl:text-8xl font-light tabular-nums"
              data-testid="calc-result-display"
            >
              {result ? fmt(result.suggested_coverage) : "$ —"}
            </motion.div>
            {result && (
              <div className="mt-6 space-y-2 text-sm text-ink/70">
                <div className="flex justify-between border-b border-ink/10 py-2"><span>Income replacement</span><span className="tabular-nums">{fmt(result.breakdown.income_replacement)}</span></div>
                <div className="flex justify-between border-b border-ink/10 py-2"><span>Debts</span><span className="tabular-nums">{fmt(result.breakdown.debts)}</span></div>
                <div className="flex justify-between border-b border-ink/10 py-2"><span>Education fund</span><span className="tabular-nums">{fmt(result.breakdown.education_fund)}</span></div>
                <div className="flex justify-between py-2"><span>Less existing coverage</span><span className="tabular-nums">−{fmt(result.breakdown.existing_coverage)}</span></div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-7 lg:pl-8">
          <div className="space-y-10">
            {rows.map((r, i) => (
              <div key={r.label} data-testid={`calc-row-${i}`}>
                <div className="flex items-baseline justify-between mb-4">
                  <label className="text-ink text-sm tracking-wide">{r.label}</label>
                  <span className="font-serif text-2xl text-ink tabular-nums">{r.fmt(r.value)}</span>
                </div>
                <Slider
                  value={[r.value]}
                  min={r.min}
                  max={r.max}
                  step={r.step}
                  onValueChange={(v) => r.set(v[0])}
                  className="[&_[role=slider]]:bg-ink [&_[role=slider]]:border-ink [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:rounded-none [&_.bg-primary]:bg-ink [&>span:first-child]:bg-ink/15"
                />
                <div className="flex justify-between mt-2 text-[10px] tracking-[0.15em] text-ink/40">
                  <span>{r.fmt(r.min)}</span>
                  <span>{r.fmt(r.max)}</span>
                </div>
              </div>
            ))}

            <button
              onClick={calculate}
              disabled={loading}
              className="w-full mt-4 py-5 bg-ink text-cream text-[12px] tracking-[0.3em] uppercase hover:bg-deepink transition-colors duration-300 disabled:opacity-60"
              data-testid="calculate-coverage-btn"
            >
              {loading ? "Calculating…" : "Calculate my coverage"}
            </button>

            {result && (
              <div className="lg:hidden pt-6 border-t border-ink/10">
                <div className="text-[10px] tracking-[0.3em] text-ink/40 mb-3">SUGGESTED COVERAGE</div>
                <div className="font-serif text-ink text-6xl font-light tabular-nums">
                  {fmt(result.suggested_coverage)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
